import { gql } from '@apollo/client';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import { toTripImageHisotryUpdateRequestAPI } from './tripImageHistoryTransformer';

import type {
  DriverNominateTripImageInput,
  DriverVisibility,
  IdentificationMethod,
  PageableContent,
  SendObstructionFeedbackInput,
  TimestampInMillis,
  TripImageHistory,
  UUID,
  VehicleTimestampImages,
} from '@cobliteam/common-types';

import type { GraphQLRestDataSourceError } from '@cobliteam/api-lib';

import graphQLRequester from '../../request/GraphQLRequester';

const toTripImage = (trip: TripImageHistory): TripImageHistory => ({
  ...trip,
  id: trip.tripId,
});

type Sort = { key: string; desc: 'asc' | 'desc' } | undefined;

export class GraphQLCameraTripImageRequester {
  private queryGetTripImagePage = gql`
    query getTripImagePage(
      $fromTimestamp: TimestampInMillis
      $toTimestamp: TimestampInMillis
      $identificationMethod: [IdentificationMethod]
      $driverVisibilities: [DriverVisibility]
      $vehicleIds: [ID]
      $driverIds: [ID]
      $isObstructed: Boolean
      $page: Int
      $size: Int
      $sort: [String]
    ) {
      tripImage(
        fromTimestamp: $fromTimestamp
        toTimestamp: $toTimestamp
        identificationMethod: $identificationMethod
        driverVisibilities: $driverVisibilities
        vehicleIds: $vehicleIds
        driverIds: $driverIds
        isObstructed: $isObstructed
        page: $page
        size: $size
        sort: $sort
      ) {
        content {
          start_timestamp
          end_timestamp
          identification_method
          driver_visibility
          nominated_at
          nominated_by {
            id
            name
          }
          recognized_driver {
            id
            name
          }
          trip_id
          trip_image_sample {
            id
            image_url
            is_obstructed
          }
          trip_images {
            image_url
            id
            is_obstructed
          }
          user_nominated_driver {
            id
            name
          }
          vehicle {
            id
            licensePlate
            groups {
              id
              name
            }
          }
        }
        empty
        first
        last
        number
        number_of_elements
        size
      }
    }
  `;

  private queryGetTripImagesDetails = gql`
    query getTripImagesDetails($tripId: String!) {
      tripImagesDetails(tripId: $tripId) {
        start_timestamp
        end_timestamp
        identification_method
        nominated_at
        nominated_by {
          id
          name
        }
        recognized_driver {
          id
          name
        }
        trip_id
        trip_image_sample {
          id
          image_url
          is_obstructed
        }
        trip_images {
          image_url
          id
          is_obstructed
          obstruction_feedback
        }
        user_nominated_driver {
          id
          name
        }
        vehicle {
          id
          licensePlate
          groups {
            id
            name
          }
        }
      }
    }
  `;

  private queryVehicleTimestampImagesDetails = gql`
    query getVehicleTimestampImagesDetails(
      $vehicleId: String!
      $imageTimestamp: Float!
      $imagesBefore: Int!
    ) {
      vehicleTimestampImagesDetails(
        vehicle_id: $vehicleId
        image_timestamp: $imageTimestamp
        images_before: $imagesBefore
      ) {
        vehicle_id
        images {
          id
          image_url
          obstruction_feedback
          is_obstructed
        }
        vehicle {
          licensePlate
          groups {
            id
            name
          }
        }
      }
    }
  `;

  private mutationDriverNominateTripImage = gql`
    mutation driverNominateTripImage($input: [DriverNominateTripImageInput!]) {
      driverNominateTripImage(input: $input)
    }
  `;

  private mutationSendObstructionFeedbackInput = gql`
    mutation sendTripImageObstructionFeedback(
      $input: SendImageObstructionFeedback!
    ) {
      sendTripImageObstructionFeedback(input: $input)
    }
  `;

  private toCamelCase = (str: string) => {
    return str.replace(/[-_]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''));
  };

  public getTripImagePage = (
    fromTimestamp?: TimestampInMillis,
    toTimestamp?: TimestampInMillis,
    identificationMethod?: IdentificationMethod[],
    driverVisibilities?: DriverVisibility[],
    vehicleIds?: UUID[],
    driverIds?: UUID[],
    isObstructed?: boolean | null,
    page?: number | unknown,
    size?: number,
    sort?: Sort
  ): Promise<PageableContent<TripImageHistory>> =>
    graphQLRequester
      .query<{
        tripImage: PageableContent<TripImageHistory>;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: this.queryGetTripImagePage,
        variables: {
          fromTimestamp,
          toTimestamp,
          identificationMethod,
          driverVisibilities,
          vehicleIds,
          driverIds,
          isObstructed,
          page,
          size,
          sort: sort ? `${this.toCamelCase(sort.key)},${sort.desc}` : null,
        },
      })
      .then((result) => {
        const tripImages = camelcaseKeys(result.tripImage, {
          deep: true,
        });
        return {
          ...tripImages,
          content: tripImages.content.map(toTripImage),
        };
      });

  public getTripImagesDetails = (tripId: string): Promise<TripImageHistory> =>
    graphQLRequester
      .query<{
        tripImagesDetails: TripImageHistory;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: this.queryGetTripImagesDetails,
        variables: {
          tripId,
        },
      })

      .then((result) => {
        const tripImages = camelcaseKeys(result.tripImagesDetails, {
          deep: true,
        });

        return tripImages;
      });

  public getVehicleTimestampImagesDetails = async (vehicleId?: string, imageTimestamp?: number, imagesBefore?: number): Promise<VehicleTimestampImages> => {
    const result = await graphQLRequester.query<{
      vehicleTimestampImagesDetails: VehicleTimestampImages;
    }>({
      query: this.queryVehicleTimestampImagesDetails,
      variables: {
        vehicleId,
        imageTimestamp,
        imagesBefore,
      },
    });

    const vehicleTimestampImages = camelcaseKeys(result.vehicleTimestampImagesDetails, { deep: true });
    return vehicleTimestampImages as VehicleTimestampImages;
  };

  public driverNominateTripImage = (input: DriverNominateTripImageInput[]): Promise<boolean> => {
    return graphQLRequester.mutate({
      mutation: this.mutationDriverNominateTripImage,
      variables: { input: toTripImageHisotryUpdateRequestAPI(input) },
    });
  };

  public sendObstructionFeedback = (input: SendObstructionFeedbackInput): Promise<boolean> => {
    return graphQLRequester.mutate({
      mutation: this.mutationSendObstructionFeedbackInput,
      variables: { input: snakecaseKeys(input) },
    });
  };
}

export default GraphQLCameraTripImageRequester;
